// previewAnimationAsset — single source of truth for "play this animation asset
// on the live avatar for a few seconds" (the content-browser right-click → Test
// action and the Inspector's Preview button).
//
// The one thing every caller MUST get right is the runtime key a clip is
// registered under. AvatarModel registers seeded server body clips on the mixer
// under their NAME; FaceClipPlayer stores seeded face clips under their name.
// Only custom uploads ALSO register the `custom_<kind>_<id>` alias (see
// clipRuntimeKeys.js / CustomAssetManager). Resolving the alias FIRST — as the
// old context-menu handler did — meant every seeded clip failed to resolve and
// "Test" silently did nothing. Resolve name-first, then the alias.
//
// Body clips play through __DIRECT_PLAY__ (the one-shot exclusive override read
// each frame by GraphEvaluator.applyWeights). Additive clips also pass a base
// pose so the preview shows the reconstructed gesture, not the raw delta —
// matching Unreal's asset-preview behaviour. Face clips play through
// FaceClipPlayer.manualPlay.

import { getLiveBodyRig } from './liveBodyRig.js'

const FACE_PREVIEW_WEIGHT = 0.8
// Last-resort hold when the clip's real length can't be read (not registered
// yet). NOT a play length for known clips — those hold for their own duration,
// so a long clip (a 4s kimodo generation, a waypoint route up to 15s) plays to
// the end instead of being cut off at a fixed 3s.
const DEFAULT_DURATION_MS = 3000
// Neutral fallbacks when an additive clip declares no explicit base pose.
const FALLBACK_BASE_CLIPS = ['idle_balanced', 'idle', 'idle_left']

// The clip's real length in ms, so the preview hold matches it instead of a
// fixed default. A splat character has no mixer registry — its clips are bound
// to the part rig, which answers the same question.
function bodyClipDurationMs(clipName) {
  const actions = (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__) || null
  const dur = actions?.[clipName]?.getClip?.().duration
    ?? getLiveBodyRig()?.clipDuration?.(clipName)
  return Number.isFinite(dur) && dur > 0 ? Math.ceil(dur * 1000) : null
}

/** Whether the live body — the GLB mixer or a splat's part rig — can play this clip.
 *  The one availability question, so no caller can answer it differently. */
export function canPlayBodyClip(clipName) {
  if (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__?.[clipName]) return true
  return getLiveBodyRig()?.clipDuration?.(clipName) != null
}

// The candidate runtime keys for an asset, name-first. Mirrors clipRuntimeKeys
// but tolerates the content-browser asset shape (rawId) as well as raw AAM rows
// (id).
function runtimeKeys(asset) {
  const id = asset.rawId ?? asset.id
  const prefix = (asset.kind === 'face_anim' || asset.kind === 'face') ? 'custom_face_' : 'custom_body_'
  const keys = []
  if (asset.name) keys.push(asset.name)
  if (id != null) keys.push(`${prefix}${id}`)
  return keys
}

function resolveBodyKey(asset) {
  const keys = runtimeKeys(asset)
  return keys.find((key) => canPlayBodyClip(key)) ?? asset.name
}

function resolveFaceKey(asset) {
  const player = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
  const keys = runtimeKeys(asset)
  return keys.find(k => player?.hasClip?.(k)) ?? asset.name
}

/**
 * Play a body/face animation asset on the live avatar for `asset.durationMs`.
 *
 * @param {object} asset - content-browser asset (or raw AAM row). Needs
 *   `kind` ('body_anim'|'face_anim'), `name`, and `rawId`/`id`. Optional
 *   `durationMs`, `additive`/`additiveType`, `basePoseAssetId`.
 * @param {object} [opts]
 * @param {Array} [opts.animationAssets] - all animation assets, used to resolve
 *   an additive clip's declared base pose by id. Optional; falls back to a
 *   neutral idle when absent.
 * @returns {boolean} true if a preview was triggered, false for unsupported kinds.
 */
export function previewAnimationAsset(asset, { animationAssets = [] } = {}) {
  if (!asset || typeof window === 'undefined') return false

  if (asset.kind === 'face_anim' || asset.kind === 'face') {
    const key = resolveFaceKey(asset)
    const durationMs = asset.durationMs || DEFAULT_DURATION_MS
    window.__FACE_CLIP_PLAYER__?.manualPlay?.(key, FACE_PREVIEW_WEIGHT, durationMs)
    return true
  }

  if (asset.kind !== 'body_anim' && asset.kind !== 'body') return false

  const additive = (asset.additiveType && asset.additiveType !== 'none') || !!asset.additive
  const clipName = resolveBodyKey(asset)
  const now = Date.now()
  // Hold the exclusive override for the clip's ACTUAL length so the whole motion
  // plays. A fixed 3s hold cut long clips short — the reported bug on kimodo
  // generations (4s default) and multi-waypoint routes (up to 15s). An explicit
  // asset.durationMs still wins; the fixed default is only the last resort when
  // the clip isn't registered on the mixer yet.
  const durationMs = asset.durationMs || bodyClipDurationMs(clipName) || DEFAULT_DURATION_MS
  const expiresAt = now + durationMs

  if (additive) {
    // Reconstruct the full gesture: play the declared base pose underneath and
    // add the delta on top, so the preview shows the gesture in context rather
    // than the raw A-pose.
    let baseClipName = null
    if (asset.basePoseAssetId) {
      const base = animationAssets.find(a => a.id === asset.basePoseAssetId)
      if (base) baseClipName = resolveBodyKey({ ...base, kind: 'body' })
    }
    if (!baseClipName) baseClipName = FALLBACK_BASE_CLIPS.find((n) => canPlayBodyClip(n)) ?? null
    window.__DIRECT_PLAY__ = { clipName, baseClipName, startTime: now, expiresAt }
  } else {
    window.__DIRECT_PLAY__ = { clipName, startTime: now, expiresAt }
  }
  return true
}

/** Release a running preview — the override, and any gesture envelope it layered. */
export function stopPreview() {
  if (typeof window === 'undefined') return
  try { window.__GESTURE_CHANNEL__?.stop?.() } catch { /* ignore */ }
  window.__DIRECT_PLAY__ = null
}

// Console + e2e test surface, mirroring __TEST_GESTURE__ / __TEST_PREFAB__:
//   __PREVIEW_ASSET__({ kind: 'face_anim', name: 'laugh_face', rawId: '…' })
// Lets a test (or a dev at the console) drive the exact preview path the
// content-browser right-click → Test and the Inspector Preview button use.
if (typeof window !== 'undefined') {
  window.__PREVIEW_ASSET__ = previewAnimationAsset
}
